/**
 * Tool AST Nodes
 *
 * Tools are STANDALONE interactive elements (NOT inline):
 * - DragOptionsTool: Draggable buttons/options
 * - NumericKeypadTool: On-screen numeric keypad
 *
 * Tools appear OUTSIDE of lines as separate UI components.
 */

import { z } from 'zod';
import { AST_TYPES } from '../constants';

// ═══════════════════════════════════════════════════════════════════════════
// Drag Options Tool
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Drag options tool schema - draggable buttons/options
 *
 * Common use cases:
 * - Comparison symbols: ["<", ">", "="]
 * - Yes/No: ["نعم", "لا"]
 * - Multiple choice: ["A", "B", "C", "D"]
 *
 * @example
 * {
 *   type: "DragOptions",
 *   options: ["<", ">", "="]
 * }
 */
export const DragOptionsToolSchema = z.object({
  type: z.literal(AST_TYPES.DRAG_OPTIONS),
  options: z.array(z.string()).min(1)
});

export type DragOptionsTool = z.infer<typeof DragOptionsToolSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Numeric Keypad Tool
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Numeric keypad tool schema - on-screen number pad
 *
 * Use cases:
 * - Touch-friendly number entry
 * - Restricting input to numbers only
 * - Consistent cross-device experience
 *
 * @example
 * {
 *   type: "NumericKeypad",
 *   includeOperators: true
 * }
 */
export const NumericKeypadToolSchema = z.object({
  type: z.literal(AST_TYPES.NUMERIC_KEYPAD),
  includeOperators: z.boolean().optional(),
  includeDecimal: z.boolean().optional(),
  style: z.enum(['diamond', 'classic']).optional()
});

export type NumericKeypadTool = z.infer<typeof NumericKeypadToolSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Geometry Tools
// ═══════════════════════════════════════════════════════════════════════════

export const RulerToolSchema = z.object({
  type: z.literal(AST_TYPES.GEOMETRY_RULER),
  size: z.number().optional().default(1)
});

export type RulerTool = z.infer<typeof RulerToolSchema>;

export const ProtractorToolSchema = z.object({
  type: z.literal(AST_TYPES.GEOMETRY_PROTRACTOR),
  size: z.number().optional().default(1)
});

export type ProtractorTool = z.infer<typeof ProtractorToolSchema>;

export const SetSquareToolSchema = z.object({
  type: z.literal(AST_TYPES.GEOMETRY_SET_SQUARE),
  size: z.number().optional().default(1)
});

export type SetSquareTool = z.infer<typeof SetSquareToolSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Union Type
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Union of all tool types
 */
export const ToolNodeSchema = z.discriminatedUnion("type", [
  DragOptionsToolSchema,
  NumericKeypadToolSchema,
  RulerToolSchema,
  ProtractorToolSchema,
  SetSquareToolSchema
]);

export type ToolNode = z.infer<typeof ToolNodeSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Factory Functions
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Create a drag options tool
 */
export function createDragOptions(options: string[]): DragOptionsTool {
  return DragOptionsToolSchema.parse({
    type: AST_TYPES.DRAG_OPTIONS,
    options
  });
}

/**
 * Create a numeric keypad tool
 */
export function createNumericKeypad(
  includeOperators?: boolean,
  includeDecimal?: boolean,
  style?: 'diamond' | 'classic'
): NumericKeypadTool {
  return NumericKeypadToolSchema.parse({
    type: AST_TYPES.NUMERIC_KEYPAD,
    includeOperators,
    includeDecimal,
    style
  });
}

export function createRulerTool(size?: number): RulerTool {
  return RulerToolSchema.parse({ type: AST_TYPES.GEOMETRY_RULER, size });
}

export function createProtractorTool(size?: number): ProtractorTool {
  return ProtractorToolSchema.parse({ type: AST_TYPES.GEOMETRY_PROTRACTOR, size });
}

export function createSetSquareTool(size?: number): SetSquareTool {
  return SetSquareToolSchema.parse({ type: AST_TYPES.GEOMETRY_SET_SQUARE, size });
}

// ═══════════════════════════════════════════════════════════════════════════
// Type Guards
// ═══════════════════════════════════════════════════════════════════════════

export function isDragOptions(value: unknown): value is DragOptionsTool {
  return DragOptionsToolSchema.safeParse(value).success;
}

export function isNumericKeypad(value: unknown): value is NumericKeypadTool {
  return NumericKeypadToolSchema.safeParse(value).success;
}

export function isRulerTool(value: unknown): value is RulerTool {
  return RulerToolSchema.safeParse(value).success;
}

export function isProtractorTool(value: unknown): value is ProtractorTool {
  return ProtractorToolSchema.safeParse(value).success;
}

export function isSetSquareTool(value: unknown): value is SetSquareTool {
  return SetSquareToolSchema.safeParse(value).success;
}

export function isToolNode(value: unknown): value is ToolNode {
  return ToolNodeSchema.safeParse(value).success;
}

